/** SUNTRONIX — Public client-facing Product Intelligence page.
 *  Unauthenticated, SUNTRONIX-branded technical view opened from a quote or a
 *  shared link. Shows APPROVED technical data only — never cost / margin /
 *  supplier / stock. Attributes the OEM source; does not imitate the OEM site. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import axios from "axios";
import {
  Cpu, ExternalLink, FileText, ShieldCheck, Award, CalendarClock, Loader2, AlertTriangle,
} from "lucide-react";

const ACCENT = "#B7803F";
const renderBold = (text) =>
  (text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="font-semibold text-white">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);

export default function PublicProductIntelligence() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [img, setImg] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/product-intelligence/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e.response?.data?.detail || "Product intelligence not available"));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center p-6" data-testid="pi-error">
      <div className="text-center">
        <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-400" />
        <div className="text-lg font-semibold">{err}</div>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-slate-500 animate-spin" />
    </div>
  );

  const images = data.images || [];
  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 overflow-x-hidden" data-testid="pi-page">
      {/* Header */}
      <div className="border-b border-slate-800/80 px-5 py-4 flex items-center justify-between">
        <div className="text-white font-semibold tracking-wide">SUNTRONIX</div>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: ACCENT }}>Product Intelligence</div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8 space-y-8">
        {/* Identity */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
          <div className="rounded-2xl bg-white p-4 flex items-center justify-center min-h-[180px]" data-testid="pi-image">
            {images.length > 0
              ? <img src={images[img]} alt={data.product_name} className="max-h-52 object-contain" />
              : <Cpu className="h-16 w-16 text-slate-300" />}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{data.brand}</div>
            <h1 className="text-2xl font-semibold text-white leading-tight mt-1" data-testid="pi-name">{data.product_name}</h1>
            <div className="mt-1 font-mono text-[13px]" style={{ color: ACCENT }} data-testid="pi-part">{data.part_number}</div>
            {data.region && <div className="mt-2 inline-block text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">{data.region}</div>}
            {images.length > 1 && (
              <div className="mt-3 flex gap-1.5">
                {images.slice(0, 6).map((u, i) => (
                  <button key={i} onClick={() => setImg(i)}
                    className={`h-10 w-10 rounded bg-white p-0.5 border ${i === img ? "border-amber-500" : "border-transparent"}`}>
                    <img src={u} alt="" className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Key highlights */}
        {data.key_config?.length > 0 && (
          <section data-testid="pi-key">
            <h2 className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Key configuration</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.key_config.map((k) => (
                <div key={k.label} className="rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{k.label}</div>
                  <div className="text-[13px] text-slate-200">{k.value}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Warranty */}
        {(data.warranty?.base || data.warranty?.included) && (
          <section data-testid="pi-warranty" className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <h2 className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Warranty & Support</h2>
            {data.warranty.base && <div className="text-[13px] text-slate-200"><span className="text-slate-500">Base: </span>{data.warranty.base}</div>}
            {data.warranty.included && <div className="text-[13px] text-slate-200 mt-1"><span className="text-slate-500">Included: </span>{data.warranty.included}</div>}
          </section>
        )}

        {/* Full specifications */}
        {data.categories?.length > 0 && (
          <section data-testid="pi-full-specs">
            <h2 className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">Full specifications</h2>
            <div className="space-y-3">
              {data.categories.map((c) => (
                <div key={c.category} className="rounded-xl border border-slate-800 overflow-hidden">
                  <div className="px-4 py-2 bg-slate-900 text-[11px] font-semibold tracking-wide text-slate-300">{c.category}</div>
                  <div className="divide-y divide-slate-800/70">
                    {(c.items || []).map((it, i) => (
                      <div key={i} className="px-4 py-2 grid grid-cols-[130px_1fr] gap-3 text-[12px]">
                        <div className="text-slate-500">{it.label}</div>
                        <div className="text-slate-300 whitespace-pre-wrap">{it.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {data.certifications && (
          <section data-testid="pi-certs" className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <h2 className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5"><Award className="h-3.5 w-3.5" /> Certifications</h2>
            <div className="text-[12px] text-slate-300">{data.certifications}</div>
          </section>
        )}

        {/* Lifecycle */}
        {(data.lifecycle?.announce_date || data.lifecycle?.end_of_support) && (
          <section data-testid="pi-lifecycle" className="flex flex-wrap gap-4 text-[12px] text-slate-400">
            <div className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Announced: {data.lifecycle.announce_date || "—"}</div>
            <div className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> End of support: {data.lifecycle.end_of_support || "—"}</div>
          </section>
        )}

        {/* Documents / source */}
        <section data-testid="pi-source" className="flex flex-wrap gap-2 pt-2">
          {data.datasheet?.url && (
            <a href={data.datasheet.url} target="_blank" rel="noreferrer" data-testid="pi-datasheet"
              className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-200">
              <FileText className="h-4 w-4" /> OEM datasheet
            </a>
          )}
          {data.source_url && (
            <a href={data.source_url} target="_blank" rel="noreferrer" data-testid="pi-source-link"
              className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-200">
              <ExternalLink className="h-4 w-4" /> View OEM source
            </a>
          )}
        </section>

        <div className="text-[10px] text-slate-600 pt-4 border-t border-slate-800/60">
          Specifications sourced from {data.source}. Presented by SUNTRONIX — your enterprise technology & lifecycle partner.
        </div>
      </div>
    </div>
  );
}
